import { environment } from 'src/environments/environment';
import { EDeviceCategory } from './device-categories';
import { EDeviceModel } from './device-models';
import { Device } from '../model/device';
import { DeviceInfo } from '../model/device-info';
import { DeviceModel } from '../model/device-model';
import { DevicesTree } from '../model/device-tree';
import { AUTOM_FW_UPDATE_IDLE, AUTOM_FW_UPDATE_IN_PROGRESS, DeviceState } from '../model/device-status';
import { Site } from '../model/site';
import { EEventIds } from './events';
import { WsEventMessage } from '../model/api/ws-event-message';

/**
 * Simula un GWETH + automazioni figlie in UI (senza hardware).
 * Lasciare a `true` solo in develop/staging. Mai su prod (`statusEnv === 'prod'`).
 */
export const MOCK_ETHERNET_GATEWAY = false;

export const MOCK_SITE_ID = 990000001;
export const MOCK_GWETH_ID = 990000039;
export const MOCK_AUTOM_ID = 990000993;
export const MOCK_AUTOM_2_ID = 990000994;

export type MockDeviceStatusPayload = {
	Id: number;
	Online: boolean;
	Configured: number;
	AutomFwUpdate: number;
	Errored: boolean;
	States: unknown[];
	ScanConnectedAutomation: boolean;
	LastSeen?: Date;
	Openings?: number;
	Closures?: number;
};

export type MockRequestResolution =
	| { kind: 'respond'; body: any }
	| { kind: 'merge-devicestatus'; realIds: number[]; mockStatuses: MockDeviceStatusPayload[] }
	| { kind: 'attach-sites' };

let mockSiteId = 0;
let mockOwnerId: number | undefined;

export const isMockEthernetGatewayEnabled = (
	mockFlag = MOCK_ETHERNET_GATEWAY,
	statusEnv = environment.statusEnv
): boolean => mockFlag === true && statusEnv !== 'prod';

export const isMockEthernetGatewayId = (id?: number | string): boolean => {
	const n = Number(id);
	return n === MOCK_GWETH_ID || n === MOCK_AUTOM_ID || n === MOCK_AUTOM_2_ID;
};

export const isMockEthernetGatewaySite = (id?: number): boolean => id === MOCK_SITE_ID;

export interface MockAutomFwTimelineStep {
	delayMs: number;
	deviceId: number;
	eventId: number;
	progress?: number;
}

let mockFamilyFwLock = false;
let mockFwTimers: ReturnType<typeof setTimeout>[] = [];

export const isMockFamilyFwLocked = (): boolean => mockFamilyFwLock;

export const setMockFamilyFwLock = (locked: boolean): void => {
	mockFamilyFwLock = locked;
};

export const resetMockEthernetGatewayFwLock = (): void => {
	mockFwTimers.forEach(timer => clearTimeout(timer));
	mockFwTimers = [];
	mockFamilyFwLock = false;
};

const mockAutomFwFlag = (): number =>
	mockFamilyFwLock ? AUTOM_FW_UPDATE_IN_PROGRESS : AUTOM_FW_UPDATE_IDLE;

export const mockAutomIdForFirmwareComponent = (component?: string): number | undefined => {
	const name = (component || '').trim().toLowerCase();
	if (!name) {
		return undefined;
	}
	if (name.includes('gweth')) {
		return MOCK_GWETH_ID;
	}
	if (name.includes('mock 2')) {
		return MOCK_AUTOM_2_ID;
	}
	return MOCK_AUTOM_ID;
};

export const buildMockAutomFwUpdateTimeline = (
	automId: number,
	gwId = MOCK_GWETH_ID
): MockAutomFwTimelineStep[] => [
	{ delayMs: 0, deviceId: automId, eventId: EEventIds.DeviceFwUpdateStarted },
	{ delayMs: 500, deviceId: gwId, eventId: EEventIds.DeviceBusyStateNotAvailable },
	{ delayMs: 1000, deviceId: automId, eventId: EEventIds.DeviceFwUpdateProgress, progress: 25 },
	{ delayMs: 2000, deviceId: automId, eventId: EEventIds.DeviceFwUpdateProgress, progress: 50 },
	{ delayMs: 3000, deviceId: automId, eventId: EEventIds.DeviceFwUpdateProgress, progress: 75 },
	{ delayMs: 4000, deviceId: automId, eventId: EEventIds.DeviceFwUpdateProgress, progress: 100 },
	{ delayMs: 6000, deviceId: automId, eventId: EEventIds.DeviceFwUpdateFinished },
	{ delayMs: 9000, deviceId: gwId, eventId: EEventIds.DeviceBusyStateAvailable },
];

export const toMockWsEvent = (step: MockAutomFwTimelineStep): WsEventMessage => {
	const deviceState: DeviceState = {
		DeviceEventId: step.eventId,
		OperationId: 0,
		CommandId: 0,
		ErrorCode: 0,
		ResponseCode: 0,
		Data: [],
		UpdatedAt: new Date(),
		DeviceId: step.deviceId,
		Progress: step.progress,
	};
	return {
		EventId: step.eventId,
		Description: '',
		Data: JSON.stringify({ DeviceEventId: step.eventId, DeviceId: step.deviceId, Progress: step.progress }),
		Category: '',
		UserId: 0,
		DeviceId: step.deviceId,
		EventDate: new Date(),
		CreatedAt: new Date(),
		Message: '',
		NotifyLevel: 0,
		StateEventDescr: '',
		PermissionEvent: undefined as unknown as WsEventMessage['PermissionEvent'],
		DeviceState: deviceState,
	};
};

export const startMockAutomFwUpdateSimulation = (
	component: string | undefined,
	dispatch: (deviceId: number, event: WsEventMessage) => void
): void => {
	resetMockEthernetGatewayFwLock();
	const automId = mockAutomIdForFirmwareComponent(component);
	if (automId == null) {
		return;
	}
	mockFamilyFwLock = true;
	for (const step of buildMockAutomFwUpdateTimeline(automId)) {
		const timer = setTimeout(() => {
			dispatch(step.deviceId, toMockWsEvent(step));
			if (step.eventId === EEventIds.DeviceBusyStateAvailable) {
				mockFamilyFwLock = false;
			}
		}, step.delayMs);
		mockFwTimers.push(timer);
	}
};

export const parseDeviceIdsQuery = (raw?: string | null): number[] => {
	if (!raw) {
		return [];
	}
	return raw
		.replace(/[[\]\s]/g, '')
		.split(',')
		.filter(Boolean)
		.map(Number)
		.filter(n => !Number.isNaN(n));
};

const rememberSite = (site: Site): void => {
	mockSiteId = site.Id ?? 0;
	mockOwnerId = site.UserId;
};

const buildGweth = (): Device => ({
	Id: MOCK_GWETH_ID,
	Name: 'GWETH mock',
	Description: 'Gateway Ethernet simulato',
	IconName: 'device-gateway-ethernet',
	ModelId: EDeviceModel.GWETH,
	SiteId: mockSiteId,
	OwnerId: mockOwnerId,
	RootId: MOCK_GWETH_ID,
	CategoryId: EDeviceCategory.Gateway,
	CurrentlyOwned: true,
});

const buildAutomation = (id: number, name: string): Device => ({
	Id: id,
	Name: name,
	Description: 'Automazione simulata sotto GWETH mock',
	IconName: 'device-swinggate',
	ModelId: EDeviceModel.ZLX24M,
	SiteId: mockSiteId,
	OwnerId: mockOwnerId,
	ParentId: MOCK_GWETH_ID,
	RootId: MOCK_GWETH_ID,
	CategoryId: EDeviceCategory.Automation,
	CurrentlyOwned: true,
});

const mockAutomations = (): Device[] => [
	buildAutomation(MOCK_AUTOM_ID, 'Automazione mock 1'),
	buildAutomation(MOCK_AUTOM_2_ID, 'Automazione mock 2'),
];

const mockTreeNode = (device: Device, children: DevicesTree[] = []): DevicesTree => ({
	Id: device.Id,
	Name: device.Name,
	Description: device.Description,
	IconName: device.IconName,
	ModelId: device.ModelId,
	ProductId: 0,
	Children: children,
});

const buildMockSite = (): Site => ({
	Id: MOCK_SITE_ID,
	Name: '[MOCK] Installazione GWETH',
	Description: 'Installazione simulata per test firmware GWETH',
	Address: '',
	Timezone: 'Europe/Rome',
	Latitude: 45.619317,
	Longitude: 12.262963,
	CurrentlyOwned: true,
	Disabled: false,
	User: undefined,
	isOnlySiteVct: false,
	Devices: [],
	DevicesTree: {
		Id: 0,
		Name: '[MOCK] Installazione GWETH',
		Description: '',
		IconName: '',
		ModelId: 0,
		ProductId: 0,
		Children: [],
	},
});

const MOCK_DEVICE_IDS = new Set([MOCK_GWETH_ID, MOCK_AUTOM_ID, MOCK_AUTOM_2_ID]);

export const attachMockEthernetGateway = (
	site: Site,
	enabled = isMockEthernetGatewayEnabled()
): Site => {
	if (!enabled || !site) {
		return site;
	}
	rememberSite(site);
	const gw = buildGweth();
	const automations = mockAutomations();
	const gwTree = mockTreeNode(gw, automations.map(a => mockTreeNode(a)));
	const existingTree = site.DevicesTree;
	const otherChildren = (existingTree?.Children || []).filter(c => !MOCK_DEVICE_IDS.has(c.Id));
	const devicesTree: DevicesTree = existingTree
		? { ...existingTree, Children: [gwTree, ...otherChildren] }
		: {
			Id: 0,
			Name: site.Name || '',
			Description: site.Description || '',
			IconName: '',
			ModelId: 0,
			ProductId: 0,
			Children: [gwTree],
		};
	return {
		...site,
		Devices: [gw, ...automations, ...(site.Devices || []).filter(d => !MOCK_DEVICE_IDS.has(d.Id))],
		DevicesTree: devicesTree,
	};
};

export const attachMockEthernetGatewayToSites = (
	sites: Site[],
	enabled = isMockEthernetGatewayEnabled()
): Site[] => {
	if (!enabled) {
		return sites;
	}
	if (!sites || sites.length === 0) {
		return [attachMockEthernetGateway(buildMockSite(), true)];
	}
	return sites.map(s => attachMockEthernetGateway(s, true));
};

const ok = (Data: unknown) => ({ Success: true, Data });

const mockStatus = (id: number): MockDeviceStatusPayload => ({
	Id: id,
	Online: true,
	Configured: 0,
	AutomFwUpdate: mockAutomFwFlag(),
	Errored: false,
	States: [],
	ScanConnectedAutomation: false,
	LastSeen: new Date(),
	Openings: 0,
	Closures: 0,
});

const mockDeviceInfo = (device: Device): DeviceInfo => ({
	Id: device.Id,
	Name: device.Name,
	Description: device.Description,
	RootId: device.RootId ?? MOCK_GWETH_ID,
	ParentId: device.ParentId,
	ModelId: device.ModelId,
	SiteId: device.SiteId,
	Serial: `MOCK-${device.Id}`,
	Keycode: `MOCK-KEY-${device.Id}`,
	BoardType: device.ModelId === EDeviceModel.GWETH ? 'GWETH' : 'ZLX24M',
	HwVersion: '1.0.0',
	FwVersion: '1.0.0',
	RemotesMax: 0,
	RootSerial: `MOCK-${MOCK_GWETH_ID}`,
	ConnType: 1,
	CrpAddress: device.CategoryId === EDeviceCategory.Automation ? 1 : undefined,
});

const mockGwethModel = (): DeviceModel => ({
	Id: EDeviceModel.GWETH,
	CategoryId: EDeviceCategory.Gateway,
	IconName: 'device-gateway-ethernet',
	MobileRemotes: false,
	LocalInputs: 0,
	LocalOutputs: 0,
	MaxAutomations: 8,
	MaxSubGateways: 0,
	AvailableServices: [],
	CategoryName: 'Gateway',
	Description: 'Gateway Ethernet (mock)',
	ProductName: 'GWETH',
	ProductBoardName: 'GWETH',
	Sharable: true,
	Vpn: false,
	SignalLevel: false,
});

const mockAutomationModel = (): DeviceModel => ({
	Id: EDeviceModel.ZLX24M,
	CategoryId: EDeviceCategory.Automation,
	IconName: 'device-swinggate',
	MobileRemotes: false,
	LocalInputs: 0,
	LocalOutputs: 0,
	MaxAutomations: 0,
	MaxSubGateways: 0,
	AvailableServices: [],
	CategoryName: 'Automation',
	Description: 'ZLX24M (mock)',
	ProductName: 'ZLX24M',
	ProductBoardName: 'ZLX24M',
	Sharable: false,
	Vpn: false,
	SignalLevel: false,
});

/** At least one feature is required: empty Features keeps the device page spinner up (`passwordRequest`). */
const mockAutomationFeatures = () => ([
	{ Name: 'commands', AccessRights: 'rw', MinFwVer: '0.0.0', Size: 0 },
]);

const mockDeviceState = (commandId: number): DeviceState => ({
	DeviceEventId: 0,
	OperationId: 0,
	CommandId: commandId,
	ErrorCode: 0,
	ResponseCode: 0,
	Data: [],
	UpdatedAt: new Date(),
	FwVersion: '1.0.0',
	HwVersion: 1,
});

const mockAutomationFirmwareUpdate = (deviceId: number) => {
	const automation = mockAutomations().find(device => device.Id === deviceId);
	return {
		Description: automation?.Name || '',
		DeviceFWVersion: '1.0.0',
		AvailableFWVersion: '1.1.0',
		AvailableUpdate: true,
	};
};

const mockFirmwareUpdates = () => ({
	Description: 'GWETH mock',
	DeviceFWVersion: '1.0.0',
	AvailableFWVersion: '1.1.0',
	AvailableUpdate: true,
	Versions: [
		{
			Description: 'GWETH mock',
			DeviceFWVersion: '1.0.0',
			AvailableFWVersion: '1.1.0',
			AvailableUpdate: true,
		},
		{
			Description: 'Automazione mock 1',
			DeviceFWVersion: '1.0.0',
			AvailableFWVersion: '1.1.0',
			AvailableUpdate: true,
		},
		{
			Description: 'Automazione mock 2',
			DeviceFWVersion: '1.0.0',
			AvailableFWVersion: '1.1.0',
			AvailableUpdate: true,
		},
	],
});

const pathnameOf = (url: string): string => {
	try {
		return new URL(url, 'https://local.invalid').pathname.replace(/\/$/, '');
	} catch {
		return (url || '').split('?')[0];
	}
};

const deviceIdsFromRequest = (url: string, devicesQuery?: string | null): number[] => {
	const fromParam = parseDeviceIdsQuery(devicesQuery);
	if (fromParam.length > 0) {
		return fromParam;
	}
	try {
		return parseDeviceIdsQuery(new URL(url, 'https://local.invalid').searchParams.get('devices'));
	} catch {
		return [];
	}
};

const deviceById = (id: number): Device | undefined => {
	if (id === MOCK_GWETH_ID) {
		return buildGweth();
	}
	return mockAutomations().find(a => a.Id === id);
};

export const mockEthernetGatewayDeviceById = (
	id?: number | string,
	enabled = isMockEthernetGatewayEnabled()
): Device | undefined => {
	if (!enabled || !isMockEthernetGatewayId(id)) {
		return undefined;
	}
	return deviceById(Number(id));
};

export const mockEthernetGatewayDeviceInfo = (
	id?: number | string,
	enabled = isMockEthernetGatewayEnabled()
): DeviceInfo | undefined => {
	const device = mockEthernetGatewayDeviceById(id, enabled);
	return device ? mockDeviceInfo(device) : undefined;
};

export const mockEthernetGatewayFeatures = () => mockAutomationFeatures();

export const resolveMockEthernetGatewayRequest = (
	method: string,
	url: string,
	devicesQuery?: string | null,
	enabled = isMockEthernetGatewayEnabled()
): MockRequestResolution | null => {
	if (!enabled) {
		return null;
	}
	const path = pathnameOf(url);

	if (method === 'GET' && (/\/sites$/.test(path) || /\/sites\/\d+$/.test(path))) {
		return { kind: 'attach-sites' };
	}

	const updatesMatch = path.match(/\/v1\/devices\/(\d+)\/updates$/);
	if (updatesMatch && isMockEthernetGatewayId(Number(updatesMatch[1]))) {
		if (method === 'GET') {
			const deviceId = Number(updatesMatch[1]);
			const body = deviceId === MOCK_GWETH_ID
				? mockFirmwareUpdates()
				: mockAutomationFirmwareUpdate(deviceId);
			return { kind: 'respond', body: ok(body) };
		}
		if (method === 'POST') {
			return { kind: 'respond', body: { Success: true } };
		}
	}

	if (method !== 'GET') {
		return null;
	}

	if (/\/devicestatus$/.test(path)) {
		const ids = deviceIdsFromRequest(url, devicesQuery);
		const mockIds = ids.filter(isMockEthernetGatewayId);
		if (mockIds.length === 0) {
			return null;
		}
		const realIds = ids.filter(id => !isMockEthernetGatewayId(id));
		const mockStatuses = mockIds.map(mockStatus);
		if (realIds.length === 0) {
			return { kind: 'respond', body: ok(mockStatuses) };
		}
		return { kind: 'merge-devicestatus', realIds, mockStatuses };
	}

	const deviceMatch = path.match(/\/devices\/(\d+)$/);
	if (deviceMatch && isMockEthernetGatewayId(Number(deviceMatch[1]))) {
		const device = deviceById(Number(deviceMatch[1]));
		return { kind: 'respond', body: ok(device) };
	}

	const gatewayMatch = path.match(/\/gateways\/(\d+)\/(status|info|signallevel)$/);
	if (gatewayMatch && Number(gatewayMatch[1]) === MOCK_GWETH_ID) {
		const action = gatewayMatch[2];
		if (action === 'status') {
			return { kind: 'respond', body: ok({ AutomFwUpdate: mockAutomFwFlag() }) };
		}
		if (action === 'info') {
			return { kind: 'respond', body: ok(mockDeviceInfo(buildGweth())) };
		}
		return { kind: 'respond', body: ok({ SignalLevel: 4, Operator: 'MOCK' }) };
	}

	const automMatch = path.match(/\/automations\/(\d+)\/(status|info|commands|features)$/);
	if (automMatch && isMockEthernetGatewayId(Number(automMatch[1]))) {
		const action = automMatch[2];
		const device = deviceById(Number(automMatch[1]));
		if (action === 'status') {
			return { kind: 'respond', body: ok({ AutomFwUpdate: mockAutomFwFlag() }) };
		}
		if (action === 'info') {
			return { kind: 'respond', body: ok(mockDeviceInfo(device)) };
		}
		if (action === 'commands') {
			return { kind: 'respond', body: ok({ Language: 'it', Commands: [] }) };
		}
		return { kind: 'respond', body: ok({ Features: mockAutomationFeatures() }) };
	}

	const automStatesMatch = path.match(/\/automations\/(\d+)\/states\/(\d+)$/);
	if (automStatesMatch && isMockEthernetGatewayId(Number(automStatesMatch[1]))) {
		return { kind: 'respond', body: ok(mockDeviceState(Number(automStatesMatch[2]))) };
	}

	const deviceStatesMatch = path.match(/\/v1\/devices\/(\d+)\/states\/(\d+)$/);
	if (deviceStatesMatch && isMockEthernetGatewayId(Number(deviceStatesMatch[1]))) {
		return { kind: 'respond', body: mockDeviceState(Number(deviceStatesMatch[2])) };
	}

	const modelMatch = path.match(/\/device-models\/model\/(\d+)\/info$/);
	if (modelMatch) {
		const modelId = Number(modelMatch[1]);
		if (modelId === EDeviceModel.GWETH) {
			return { kind: 'respond', body: ok(mockGwethModel()) };
		}
		if (modelId === EDeviceModel.ZLX24M) {
			return { kind: 'respond', body: ok(mockAutomationModel()) };
		}
	}

	return null;
};
